import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { applicationsAPI, profileAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import DocumentUpload from '@/components/DocumentUpload';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FileText,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  Building,
  MapPin,
  Calendar,
  ArrowRight,
  Loader2,
  Plus,
  Filter,
  Upload,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedApp, setExpandedApp] = useState(null);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [selectedAppForDocs, setSelectedAppForDocs] = useState(null);
  const [educationLevel, setEducationLevel] = useState('bachelors');

  const fetchProfile = useCallback(async () => {
    try {
      const response = await profileAPI.get();
      if (response.data?.education_level) {
        setEducationLevel(response.data.education_level);
      }
    } catch (error) {
      // Profile might not exist yet
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const response = await applicationsAPI.getAll();
      setApplications(response.data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchApplications();
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchApplications, fetchProfile]);

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-600',
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-amber-100 text-amber-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      enrolled: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || colors.draft;
  };

  const getStatusIcon = (status) => {
    if (status === 'accepted' || status === 'enrolled') return <CheckCircle className="w-4 h-4" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4" />;
    return <Clock className="w-4 h-4" />;
  };

  const filteredApplications = applications.filter(app =>
    app.course_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.university_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUploadDocs = (app) => {
    setSelectedAppForDocs(app);
    setShowDocUpload(true);
  };

  const handleDocumentsChange = (docs) => {
    // Refresh applications to show updated documents
    fetchApplications();
  };

  const toggleExpand = (appId) => {
    setExpandedApp(expandedApp === appId ? null : appId);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <div className="w-20 h-20 rounded-full bg-teal-600/10 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-teal-600" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-800 dark:text-white mb-4">Track Your Applications</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              Sign in to view and manage your university applications in one place
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/login')}
                className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="login-to-view"
              >
                Sign In
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/register')}
                variant="outline"
                className="border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Create Account
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-100/80 dark:from-slate-800 via-[#e9ecef] dark:via-slate-800 to-[#f1f3f5] dark:to-slate-900 py-12 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-4xl font-semibold text-gray-800 dark:text-white mb-2">My Applications</h1>
                <p className="text-gray-600 dark:text-gray-300">Track the status of all your university applications</p>
              </div>
              <Link to="/courses">
                <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="new-application">
                  <Plus className="w-4 h-4 mr-2" />
                  New Application
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Application Overview</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Track your application progress</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">{applications.length}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {applications.filter(a => ['submitted', 'under_review'].includes(a.status)).length}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {applications.filter(a => a.status === 'accepted').length}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Accepted</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                      {applications.filter(a => a.status === 'enrolled').length}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Enrolled</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Applications List - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Search className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Your Applications</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Search and manage your submissions</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
              <Input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 focus:border-teal-500 dark:text-white"
                data-testid="search-applications"
              />
            </div>

            {/* Applications List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-slate-600 flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-10 h-10 text-gray-500 dark:text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">No Applications Yet</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Start exploring courses and submit your first application
                </p>
                <Link to="/courses">
                  <Button className="bg-teal-600 text-white hover:bg-teal-700 font-semibold">
                    Browse Courses
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredApplications.map((app) => (
                  <div 
                    key={app.id} 
                    className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden hover:border-teal-400 dark:hover:border-teal-500 transition-colors"
                    data-testid={`application-item-${app.id}`}
                  >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={getStatusColor(app.status)}>
                            {getStatusIcon(app.status)}
                            <span className="ml-1 capitalize">{app.status?.replace('_', ' ')}</span>
                          </Badge>
                          {app.documents?.length > 0 && (
                            <Badge className="bg-blue-100 text-blue-600">
                              {app.documents.length} doc{app.documents.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">{app.course_name}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            {app.university_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {app.country}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(app.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={() => handleUploadDocs(app)}
                          variant="outline"
                          className="border-teal-400 text-teal-600 hover:bg-teal-600/10"
                          data-testid={`upload-docs-${app.id}`}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Docs
                        </Button>
                        <Button
                          onClick={() => toggleExpand(app.id)}
                          variant="outline"
                          className="border-gray-300 text-gray-600 hover:bg-gray-100"
                        >
                          {expandedApp === app.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Section - Show Documents */}
                  {expandedApp === app.id && (
                    <div className="border-t border-gray-200 dark:border-slate-600 p-6 bg-white dark:bg-slate-800">
                      <h4 className="text-gray-800 dark:text-white font-medium mb-4">Uploaded Documents</h4>
                      {app.documents?.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {app.documents.map((doc) => (
                            <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-slate-700 rounded-lg">
                              <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-800 dark:text-white text-sm font-medium truncate">{doc.filename}</p>
                                <p className="text-gray-500 dark:text-gray-400 text-xs capitalize">{doc.type?.replace('_', ' ')}</p>
                              </div>
                              <Badge className="bg-green-100 text-green-600 text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Uploaded
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">No documents uploaded yet. Click &quot;Upload Docs&quot; to add your documents.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Document Upload Dialog */}
      <Dialog open={showDocUpload} onOpenChange={setShowDocUpload}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Upload Documents</DialogTitle>
          </DialogHeader>
          {selectedAppForDocs && (
            <div className="mt-4">
              <div className="mb-4 p-3 bg-[#e9ecef]/50 rounded-lg">
                <p className="text-teal-600 font-medium">{selectedAppForDocs.course_name}</p>
                <p className="text-gray-500 text-sm">{selectedAppForDocs.university_name}</p>
              </div>
              <DocumentUpload 
                applicationId={selectedAppForDocs.id}
                educationLevel={educationLevel}
                onDocumentsChange={handleDocumentsChange}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ApplicationsPage;
